import type { RangeCalculator, LockedRange } from './types';

/**
 * 预设：锁定函数签名（TypeScript/JavaScript）
 * 锁定 interface 定义、function 签名行和最后的闭合括号
 */
export const lockFunctionSignatureTS: RangeCalculator = (code: string): LockedRange[] => {
  const lines = code.split('\n');
  const ranges: LockedRange[] = [];

  // 找到 function 定义行
  let functionStartLine = -1;
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (/^\s*(async\s+)?function\s+\w+/.test(line)) {
      functionStartLine = i + 1; // Monaco 行号从 1 开始
      // 锁定从开头到 function { 这一行（包含所有 interface 和 function 签名）
      ranges.push({
        startLine: 1,
        endLine: functionStartLine,
        description: 'Interface definitions and function signature',
      });
      break;
    }
  }

  // 锁定最后的 } 行
  for (let i = lines.length - 1; i >= 0; i--) {
    if (lines[i].trim() === '}') {
      ranges.push({
        startLine: i + 1,
        endLine: i + 1,
        description: 'Function closing brace',
      });
      break;
    }
  }

  return ranges;
};

/**
 * 预设：锁定函数签名（Python）
 * 锁定 TypedDict 定义和 def 签名行
 */
export const lockFunctionSignaturePython: RangeCalculator = (code: string): LockedRange[] => {
  const lines = code.split('\n');
  const ranges: LockedRange[] = [];

  // Python: 锁定从开头到 def 行（包含 def 行）
  const defIndex = lines.findIndex(line => /^\s*(async\s+)?def\s+/.test(line));
  if (defIndex !== -1) {
    ranges.push({
      startLine: 1,
      endLine: defIndex + 1,
      description: 'TypedDict definitions and function signature',
    });
  }

  return ranges;
};

/**
 * 预设：锁定前 N 行
 */
export const lockFirstNLines = (n: number): RangeCalculator => {
  return (code: string): LockedRange[] => {
    const totalLines = code.split('\n').length;
    if (n <= 0 || n > totalLines) return [];
    
    return [{
      startLine: 1,
      endLine: n,
      description: `First ${n} lines`,
    }];
  };
};

/**
 * 预设：锁定后 N 行
 */
export const lockLastNLines = (n: number): RangeCalculator => {
  return (code: string): LockedRange[] => {
    const totalLines = code.split('\n').length;
    if (n <= 0 || n > totalLines) return [];
    
    return [{
      startLine: totalLines - n + 1,
      endLine: totalLines,
      description: `Last ${n} lines`,
    }];
  };
};

/**
 * 预设：锁定指定行范围
 */
export const lockLineRange = (start: number, end: number): RangeCalculator => {
  return (): LockedRange[] => [{
    startLine: start,
    endLine: end,
    description: `Lines ${start}-${end}`,
  }];
};

/**
 * 预设：通过正则表达式锁定匹配的行
 */
export const lockLinesByRegex = (pattern: RegExp, options?: {
  lockEntireMatch?: boolean; // 是否锁定整个匹配块（从匹配开始到下一个空行）
}): RangeCalculator => {
  return (code: string): LockedRange[] => {
    const lines = code.split('\n');
    const ranges: LockedRange[] = [];

    for (let i = 0; i < lines.length; i++) {
      if (pattern.test(lines[i])) {
        if (options?.lockEntireMatch) {
          // 找到匹配块的结束位置（下一个空行或文件末尾）
          let endLine = i + 1;
          for (let j = i + 1; j < lines.length; j++) {
            if (lines[j].trim() === '') break;
            endLine = j + 1;
          }
          ranges.push({
            startLine: i + 1,
            endLine,
            description: `Block starting at line ${i + 1}`,
          });
        } else {
          ranges.push({
            startLine: i + 1,
            endLine: i + 1,
            description: `Line ${i + 1}`,
          });
        }
      }
    }

    return ranges;
  };
};

/**
 * 预设：组合多个计算器
 */
export const combineCalculators = (...calculators: RangeCalculator[]): RangeCalculator => {
  return (code: string, context?: any): LockedRange[] => {
    const allRanges: LockedRange[] = [];
    for (const calculator of calculators) {
      allRanges.push(...calculator(code, context));
    }
    return allRanges;
  };
};

/**
 * 预设映射表
 */
export const PRESET_CALCULATORS: Record<string, RangeCalculator> = {
  'typescript-function': lockFunctionSignatureTS,
  'javascript-function': lockFunctionSignatureTS,
  'python-function': lockFunctionSignaturePython,
};

/**
 * 根据语言类型获取函数签名锁定预设
 */
export const getFunctionSignatureLock = (language: string): RangeCalculator => {
  const lang = language.toLowerCase();
  if (lang === 'python') {
    return lockFunctionSignaturePython;
  }
  return lockFunctionSignatureTS;
};
